import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Select from "react-select";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormTextarea,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
} from "@coreui/react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {
  getRouteList,
  getSearchAbleRoutes,
} from "../../services/route.services";
import {
  getSearchedBookingOrderList,
  getSingleBookingOrder,
} from "../../services/booking.services";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import {
  assignOrdersToTrip,
  createTrip,
  getSingleTrip,
  getTripList,
} from "src/services/trip.services";
import { getSearchedUser } from "src/services/user.services";
import moment from "moment";
import ReactDatePicker from "react-datepicker";
import { getActiveCountryList } from "src/services/country.services";
import { useMutation, useQuery } from "@tanstack/react-query";
import TripTableData from "./TripTableData";
import useTableSort from "src/__custom_hook/useTableSort";

const schema = z.object({
  orderIds: z.array(z.string()).refine((data) => data.length > 0, {
    message: "OrderIds must be a non-empty array of strings",
  }),
  tripName: z.string().optional(),
  tripId: z.string().min(1, "Required"),
  bookingOrderId: z.string().min(1, "Required"),
  tripRoute: z.string().min(1, "Required"),
  tripStartTime: z.string().min(1, "Required"),
  tripAssignedBy: z.string().optional(),
  // tripVehicleId: z.string().min(1, "Required"),
  //tripTagId: z.string().min(1, "Required"),
  tripAssignedTo: z.string().min(1, "Required"),
  tripRemarks: z.string().optional(),
  country: z.string().min(1, "Required"),
  priority: z.string(),
});

const CreateTrip = ({ id: propId, tripId, handleCloseModal }) => {
  const { sortConfig, handleClickColumnSort } = useTableSort();

  const [bookingOrderOptions, setBookingOrderOptions] = useState([]);
  const [selectedBookingOrderId, setSelectedBookingOrderId] = useState("");
  const [bookingOrderData, setBookingOrderData] = useState({});
  const [bookingOrderArray, setBookingOrderArray] = useState([]);
  const [selectedId, setSelectedId] = useState("");

  const [routeOptions, setRouteOptions] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [routeIdArray, setRouteIdArray] = useState(null);

  const [driverOptions, setDriverOptions] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [driverIdArray, setDriverIdArray] = useState([]);
  const [allDriverData, setAllDriverData] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);
  const [showTripListModal, setShowTripListModal] = useState(false);

  const [user, setUser] = useState(
    localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null
  );
  const { id: paramsId } = useParams();
  const id = propId || paramsId;

  const navigate = useNavigate();
  const [defaultErrorMessage, setDefaultErrorMessage] = useState(
    '"Something went wrong. Please try again"'
  );

  const openTripQuery = useQuery(
    {
      queryKey: ["open-trip"],
      queryFn: () =>
        getTripList({
          limit: 100,
          page: 1,
          tripStatus: "assigned,inProgress",
        }),
      enabled: showTripListModal, // Refactored line
    },
    {
      refetchOnWindowFocus: false, // Added option to prevent unnecessary refetching
    }
  );

  const getSelectedTripInfo = (tripId) => {
    getSingleTrip(tripId)
      .then((response) => {
        const fetchedTripData = response.data;
        // setTripData(fetchedTripData);
        console.log(fetchedTripData);

        setValue("tripName", fetchedTripData.tripName);
        setValue("tripId", fetchedTripData.tripId);
        setValue("orderIds", fetchedTripData.orderIds);
        setValue("bookingOrderId", fetchedTripData.bookingOrderId);
        setValue("tripRoute", fetchedTripData.tripRoute);
        setValue("tripStartTime", fetchedTripData.tripStartTime);
        setValue("tripAssignedBy", fetchedTripData.tripAssignedBy);
        setValue("tripTagId", fetchedTripData.tripTagId);
        // setValue("tripVehicleId", fetchedTripData.tripVehicleId);
        setValue("tripAssignedTo", fetchedTripData.tripAssignedTo);
        setValue("tripRemarks", fetchedTripData.tripRemarks);
        setValue("country", fetchedTripData.country);
        setValue("priority", fetchedTripData.priority);

        if (fetchedTripData.tripRoute) {
          setSelectedRouteId({
            value: fetchedTripData.tripRoute._id,
            label: fetchedTripData.tripRoute.name,
          });
          setValue("tripRoute", fetchedTripData.tripRoute._id);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const {
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      tripName: "",
      tripId: "TRI" + new Date().getTime(),
      orderIds: [],
      bookingOrderId: "",
      tripRoute: "",
      tripStartTime: `${new Date().getHours()}:${new Date().getMinutes()}`,
      tripAssignedBy: "",
      // tripVehicleId: "",
      tripTagId: "",
      tripAssignedTo: "",
      tripRemarks: "",
      country: selectedCountry ? selectedCountry.value : "",
      priority: "normal",
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = (data) => {
    toast
      .promise(
        createTrip(data),
        {
          loading: "Loading",
          success: "Trip assigned Succesfully",
          error: (err) => {
            return err.response &&
              err.response.data &&
              err.response.data.message
              ? err.response.data.message
              : defaultErrorMessage;
          },
        },
        {
          id: "assign-trip",
        }
      )
      .then(async (response) => {
        reset();
        customFormReset();
        navigate("/create-trip", {
          replace: true,
        });
      })
      .catch((error) => console.error(error));
  };

  const getActiveCountries = () => {
    getActiveCountryList({ limit: 100, page: 1, status: true })
      .then((response) => {
        if (response.success && response.data) {
          // setCountries(response.data);
          let options = response.data.map((element) => ({
            value: `${element._id}`,
            label: `${element.name}`,
          }));
          setCountryOptions(options);
        }
      })
      .catch((error) => console.log(error));
  };

  const customFormReset = () => {
    setValue("tripId", "TRI" + new Date().getTime());
    setValue("orderIds", []);
    getBookingOrderList();

    setSelectedBookingOrderId("");
    setValue("bookingOrderId", "");

    setSelectedRouteId("");
    setValue("tripRoute", "");

    setSelectedDriverId("");
    setValue("tripAssignedTo", "");
    setSelectedId(null);
    setBookingOrderData({
      fromLocation: { name: "" },
      toLocation: { name: "" },
      transferOrderNo: "",
      sealBag: { serialNo: "" },
      pickupDate: moment(),
      pickupTime: "",
    });

    if (user && user.userType === "superAdmin") {
      setSelectedCountry("");
      setValue("country", "");
    }
  };

  const onError = (err) => {
    console.error(err);
  };

  const handleChangeBookingOrder = (selectedOption) => {
    if (selectedOption) {
      setSelectedBookingOrderId(selectedOption);
      setValue("orderIds", [selectedOption.value]);
      setValue("bookingOrderId", selectedOption.value);
      getSelectedBookingOrderInfo(selectedOption.value);
    } else {
      setSelectedBookingOrderId("");
      setValue("bookingOrderId", "");
      setValue("orderIds", []);
      setBookingOrderData({
        fromLocation: { name: "" },
        toLocation: { name: "" },
        transferOrderNo: "",
        sealBag: { serialNo: "" },
        pickupDate: moment(),
        pickupTime: "",
      });
      setSelectedRouteId("");
      setValue("tripRoute", "");
      setSelectedId(null);
    }
  };

  const getSelectedBookingOrderInfo = (id) => {
    getSingleBookingOrder(id)
      .then(async (response) => {
        const fetchedBookingOrderData = response.data;
        if (
          fetchedBookingOrderData.priority &&
          fetchedBookingOrderData.priority === "emergency"
        ) {
          setValue("priority", "emergency");
          getCountryEmergencyRoute(
            "emergency",
            fetchedBookingOrderData.country
          );
        }
        setBookingOrderData(fetchedBookingOrderData);
        setValue("orderIds", [fetchedBookingOrderData._id]);
        if (fetchedBookingOrderData?.tripRoute) {
          setSelectedRouteId({
            value: fetchedBookingOrderData.tripRoute._id,
            label: fetchedBookingOrderData.tripRoute.name,
          });
          setValue("tripRoute", fetchedBookingOrderData.tripRoute._id);
        } else {
          setSelectedRouteId("");
          setValue("tripRoute", "");
        }
        setSelectedBookingOrderId({
          label: fetchedBookingOrderData.bookingOrderId,
          value: fetchedBookingOrderData._id,
        });
        setValue("bookingOrderId", fetchedBookingOrderData.bookingOrderId);
        setSelectedId(fetchedBookingOrderData._id);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleChangeRoute = (selectedOption) => {
    if (selectedOption) {
      setSelectedRouteId(selectedOption);
      setValue("tripRoute", selectedOption.value);
    } else {
      setSelectedRouteId("");
      setValue("tripRoute", "");
    }
  };

  const getRouteNameList = (searchString = null, country = null) => {
    getSearchAbleRoutes({ searchString, country })
      .then(async (response) => {
        const fetchedRouteData = response.data;
        let options = [];
        let routeIdArray = [];

        fetchedRouteData.forEach((element) => {
          const option = { value: `${element._id}`, label: `${element.name}` };
          options.push(option);
          routeIdArray.push(element._id);
        });
        setRouteIdArray(routeIdArray);
        setRouteOptions(options);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleOnInputChangeRoute = (searchValue) => {
    if (searchValue) {
      const matchingRoutes = routeOptions.filter((route) =>
        route.label.toLowerCase().includes(searchValue.toLowerCase())
      );
      if (matchingRoutes.length <= 0) {
        getRouteNameList(searchValue);
      }
    }
  };

  const handleOnInputChangeBookingOrder = (selectedOption) => {
    if (selectedOption) {
      const expectedData = bookingOrderArray.find((a) =>
        a.includes(selectedOption)
      );
      if (!expectedData) {
        getBookingOrderList(selectedOption, selectedCountry.value);
      }
    }
  };

  const getBookingOrderList = (searchedString = null, country = null) => {
    const params = {
      limit: 10,
      page: 1,
      type: "manage",
      status: "cvit",
    };

    if (searchedString) params.bookingOrderId = searchedString;
    if (country) params.country = country;

    getSearchedBookingOrderList(params)
      .then(async (response) => {
        const fetchedBookingOrderData = response.data;
        let options = [];
        let bookingOrderIdArray = [];

        fetchedBookingOrderData.forEach((element) => {
          const option = {
            value: `${element._id}`,
            label: `${element.bookingOrderId}`,
          };
          options.push(option);
          bookingOrderIdArray.push(element.bookingOrderId);
        });
        setBookingOrderArray(bookingOrderIdArray);
        setBookingOrderOptions(options);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleChangeDriver = (selectedOption) => {
    if (selectedOption) {
      setSelectedDriverId(selectedOption);
      setValue("tripAssignedTo", selectedOption.value);
      const driver = allDriverData.find((x) => x._id === selectedOption.value);

      if (driver && driver.tagId) {
        setValue("tripTagId", driver?.tagId);
      }
    } else {
      setSelectedDriverId("");
      setValue("tripRtripAssignedTooute", "");
    }
  };

  const handleOnInputChangeDriver = (selectedOption) => {
    if (selectedOption) {
      const expectedData = driverIdArray.find((a) =>
        a.includes(selectedOption)
      );
      if (!expectedData) {
        getSearchedUserList(selectedOption);
      }
    }
  };

  const getSearchedUserList = (value = null, country = null) => {
    const params = {
      limit: 10,
      page: 1,
      type: "driver",
      availibility: true,
    };

    if (value) params.name = value;
    if (country) params.country = country;

    getSearchedUser(params)
      .then(async (response) => {
        const fetchedDriverData = response.data;
        setAllDriverData(fetchedDriverData);
        let options = [];
        let driverIdArray = [];

        fetchedDriverData.forEach((element) => {
          const option = {
            value: `${element._id}`,
            label: `${
              element.firstName +
              " " +
              element.lastName +
              "-" +
              element.employeeCode
            }`,
          };
          options.push(option);
          driverIdArray.push(element._id);
        });
        setDriverIdArray(driverIdArray);
        setDriverOptions(options);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleCountryChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedCountry(selectedOption);
      setValue("country", selectedOption.value);
      getRouteNameList(null, selectedOption.value);
      getSearchedUserList(null, selectedOption.value);
      getBookingOrderList(null, selectedOption.value);
    } else {
      setSelectedCountry(null);
      setValue("country", "");
      getRouteNameList();
      getSearchedUserList();
      getBookingOrderList();
    }
  };

  const getCountryEmergencyRoute = async (tripUrgency, country) => {
    try {
      const response = await getRouteList({ tripUrgency, country });
      if (response?.success && response?.data.length > 0) {
        const routeInfo = response.data[0];
        setSelectedRouteId({
          label: routeInfo.name,
          value: routeInfo._id,
        });
        setValue("tripRoute", routeInfo._id);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const assignToTripMutation = useMutation({
    mutationKey: ["assignToTrip"],
    mutationFn: (data) => assignOrdersToTrip(data),
    onMutate: () => {
      toast.loading("Assigning Orders...", { id: "assign-trip" });
    },
    onSuccess: (res) => {
      toast.dismiss("assign-trip");
      const data = res.data;
      if (data.success) {
        toast.success(data.message);
        setShowTripListModal(false);
        if (tripId && handleCloseModal) {
          handleCloseModal();
        } else {
          // navigate("/manage-trip");
          navigate(-1);
          window.open("/manage-trip");
        }
      }
    },
    onSettled: () => {
      toast.dismiss("assign-trip");
    },
  });

  const onRowClick = (tripId) => {
    const payload = {
      tripId,
      orderIds: [id],
    };

    assignToTripMutation.mutate(payload);
  };

  useEffect(() => {
    if (id) {
      getSelectedBookingOrderInfo(id);
    }
  }, [id]);

  useEffect(() => {
    if (tripId) {
      getSelectedTripInfo(tripId);
    }
  }, [tripId]);

  useEffect(() => {
    if (!id && !tripId) {
      console.log("i am here");
      getBookingOrderList();
    }
  }, [id, tripId]);

  useEffect(() => {
    if (user) setValue("tripAssignedBy", user.id);

    if (user?.country) {
      setValue("country", user.country._id);
      setSelectedCountry({ value: user.country._id, label: user.country.name });
    }

    if (user && user.userType === "superAdmin") getActiveCountries();

    getRouteNameList();
    getSearchedUserList();
  }, []);

  return (
    <>
      <CCard className="mb-4">
        <CCardBody>
          {!propId && (
            <>
              <CRow>
                <CCol sm={5}>
                  <h4 id="traffic" className="card-title mb-0">
                    Create New Trip
                  </h4>
                  {/* <div className="small text-medium-emphasis">January - July 2021</div> */}
                </CCol>
                <CCol sm={7} className="d-none d-md-block"></CCol>
              </CRow>
              <hr />
              <br />
            </>
          )}

          <CForm onSubmit={handleSubmit(onSubmit, onError)}>
            <CRow>
              <CCol sm={6}>
                {user && user.userType === "superAdmin" && (
                  <CRow className="mb-3">
                    <CFormLabel className="col-sm-5">Country:</CFormLabel>
                    <CCol sm={7}>
                      <Select
                        value={selectedCountry}
                        onChange={handleCountryChange}
                        options={countryOptions}
                        isClearable={true}
                        isDisabled={user.userType !== "superAdmin"}
                      />
                      {errors.country?.message && (
                        <p className="error_text">{errors.country?.message}</p>
                      )}
                    </CCol>
                  </CRow>
                )}
                {["admin", "cvit"].includes(user?.userType) && (
                  <CRow className="mb-3">
                    <CCol md={5}></CCol>
                    <CCol md={7}>
                      <CButton
                        color="btn-purple"
                        className="btn-purple w-100"
                        onClick={() => setShowTripListModal(true)}
                      >
                        View Open Trips
                      </CButton>
                    </CCol>
                  </CRow>
                )}
                <CRow className="mb-3">
                  <CCol sm={5}>
                    <CFormLabel
                      htmlFor="tripId"
                      className="col-sm-3 col-form-label w-100"
                    >
                      Trip Name:
                    </CFormLabel>
                  </CCol>

                  <CCol sm={7}>
                    <CFormInput
                      type="text"
                      id="tripName"
                      {...register("tripName")}
                    />
                    {errors.tripName?.message && (
                      <p className="error_text">{errors.tripName?.message}</p>
                    )}
                  </CCol>
                </CRow>
                <CRow className="mb-3">
                  <CCol sm={5}>
                    <CFormLabel
                      htmlFor="tripId"
                      className="col-sm-3 col-form-label w-100"
                    >
                      Trip ID:
                    </CFormLabel>
                  </CCol>

                  <CCol sm={7}>
                    <CFormInput
                      type="text"
                      id="tripId"
                      {...register("tripId")}
                      disabled={true}
                    />
                    {errors.tripId?.message && (
                      <p className="error_text">{errors.tripId?.message}</p>
                    )}
                  </CCol>
                </CRow>

                {!tripId && (
                  <>
                    <CRow className="mb-3">
                      <CCol sm={5}>
                        <CFormLabel
                          htmlFor="bookingOrderId"
                          className="col-sm-3 col-form-label w-100"
                        >
                          Booking Order ID:
                        </CFormLabel>
                      </CCol>

                      <CCol sm={7}>
                        <Select
                          disabled
                          value={selectedBookingOrderId}
                          onChange={handleChangeBookingOrder}
                          onInputChange={handleOnInputChangeBookingOrder}
                          options={bookingOrderOptions}
                          isClearable={true}
                          isDisabled={id || !selectedCountry}
                        />
                        {errors.bookingOrderId?.message && (
                          <p className="error_text">
                            {errors.bookingOrderId?.message}
                          </p>
                        )}
                      </CCol>
                    </CRow>

                    <CRow className="mb-3">
                      <CCol sm={5}>
                        <CFormLabel
                          htmlFor="orderNumber"
                          className="col-sm-3 col-form-label w-100"
                        >
                          From Location Name:
                        </CFormLabel>
                      </CCol>

                      <CCol sm={7}>
                        <CFormInput
                          disabled
                          type="text"
                          id="fromLocation"
                          value={bookingOrderData?.fromLocation?.name}
                        />
                      </CCol>
                    </CRow>
                    <CRow className="mb-3">
                      <CCol sm={5}>
                        <CFormLabel
                          htmlFor="orderNumber"
                          className="col-sm-3 col-form-label w-100"
                        >
                          To Location Name:
                        </CFormLabel>
                      </CCol>
                      <CCol sm={7}>
                        <CFormInput
                          disabled
                          type="text"
                          id="toLocation"
                          value={bookingOrderData?.toLocation?.name}
                        />
                      </CCol>
                    </CRow>
                    <CRow className="mb-3 d-flex justify-content-between">
                      <CCol sm={5}>
                        <CFormLabel
                          htmlFor="orderNumber"
                          className="col-sm-3 col-form-label w-100"
                        >
                          Transfer Order No.:
                        </CFormLabel>
                      </CCol>
                      <CCol sm={7}>
                        <CFormInput
                          disabled
                          type="text"
                          id="toNumber"
                          value={bookingOrderData?.transferOrderNo}
                        />
                      </CCol>
                    </CRow>
                    <CRow className="mb-3 d-flex justify-content-between">
                      <CCol sm={5}>
                        <CFormLabel
                          htmlFor="orderNumber"
                          className="col-sm-3 col-form-label w-100"
                        >
                          Seal/Bag No.:
                        </CFormLabel>
                      </CCol>
                      <CCol sm={7}>
                        <CFormInput
                          disabled
                          type="text"
                          id="sealNumber"
                          value={bookingOrderData?.sealBag?.serialNo}
                        />
                      </CCol>
                    </CRow>
                  </>
                )}
                <CRow className="mb-3">
                  <CFormLabel
                    htmlFor="trip"
                    className="col-sm-5 col-form-label"
                  >
                    Route Name:
                  </CFormLabel>
                  <CCol sm={7}>
                    <Select
                      value={selectedRouteId}
                      onChange={handleChangeRoute}
                      onInputChange={handleOnInputChangeRoute}
                      options={routeOptions}
                      isClearable={true}
                      isDisabled={true}
                    />
                    {errors.tripRoute?.message && (
                      <p className="error_text">{errors.tripRoute?.message}</p>
                    )}
                  </CCol>
                </CRow>
              </CCol>

              <CCol sm={6}>
                {["superAdmin"].includes(user?.userType) && (
                  <CRow className="mb-3">
                    <CCol md={4}></CCol>
                    <CCol md={7}>
                      <CButton
                        color="btn-purple"
                        className="btn-purple w-100"
                        onClick={() => setShowTripListModal(true)}
                      >
                        View Open Trips
                      </CButton>
                    </CCol>
                  </CRow>
                )}
                <CRow className="mb-3">
                  <CCol sm={4}>
                    <CFormLabel
                      htmlFor="orderNumber"
                      className="col-sm-3 col-form-label w-100"
                    >
                      Pick-up Date.:
                    </CFormLabel>
                  </CCol>
                  <CCol sm={7}>
                    <ReactDatePicker
                      dateFormat="dd/MM/yyyy"
                      className="form-control"
                      value={moment(bookingOrderData?.pickupDate).format(
                        "DD/MM/YYYY"
                      )}
                      disabled
                    />
                  </CCol>
                </CRow>
                <CRow className="mb-3">
                  <CCol sm={4}>
                    <CFormLabel
                      htmlFor="orderNumber"
                      className="col-sm-3 col-form-label w-100"
                    >
                      Pick-up Time:
                    </CFormLabel>
                  </CCol>
                  <CCol sm={7}>
                    <CFormInput
                      disabled
                      type="time"
                      id="pickupTime"
                      value={
                        bookingOrderData?.pickupTime ||
                        `${new Date().getHours()}:${new Date().getMinutes()}`
                      }
                    />
                  </CCol>
                </CRow>
                <CRow className="mb-3">
                  <CFormLabel
                    htmlFor="tripStartTime"
                    className="col-sm-4 col-form-label"
                  >
                    Start Time:
                  </CFormLabel>
                  <CCol sm={7}>
                    <CFormInput
                      type="time"
                      id="tripStartTime"
                      {...register("tripStartTime")}
                    />
                    {errors.tripStartTime?.message && (
                      <p className="error_text">
                        {errors.tripStartTime?.message}
                      </p>
                    )}
                  </CCol>
                </CRow>
                <CRow className="mb-3">
                  <CFormLabel
                    htmlFor="tripAssignedBy"
                    className="col-sm-4 col-form-label"
                  >
                    CVIT Officer:
                  </CFormLabel>
                  <CCol sm={7}>
                    <CFormInput
                      disabled
                      type="text"
                      id="tripAssignedBy"
                      value={user.firstName + " " + user.lastName}
                    />
                  </CCol>
                </CRow>
                <CRow className="mb-3">
                  <CFormLabel
                    htmlFor="tripAssignedTo"
                    className="col-sm-4 col-form-label"
                  >
                    Driver:
                  </CFormLabel>
                  <CCol sm={7}>
                    <Select
                      onInputChange={handleOnInputChangeDriver}
                      value={selectedDriverId}
                      onChange={handleChangeDriver}
                      options={driverOptions}
                      isClearable={true}
                      isDisabled={!selectedCountry}
                    />
                    {errors.tripAssignedTo?.message && (
                      <p className="error_text">
                        {errors.tripAssignedTo?.message}
                      </p>
                    )}
                  </CCol>
                </CRow>
                <CRow className="mb-3">
                  {/* <CFormLabel
                    htmlFor="tripVehicleId"
                    className="col-sm-4 col-form-label"
                  >
                    Vehicle ID:
                  </CFormLabel> */}
                  <CFormLabel
                    htmlFor="tripTagId"
                    className="col-sm-4 col-form-label"
                  >
                    Tag ID:
                  </CFormLabel>
                  <CCol sm={7}>
                    <CFormInput
                      type="text"
                      id="tripTagId"
                      {...register("tripTagId")}
                      disabled={true}
                    />
                    {errors.tripTagId?.message && (
                      <p className="error_text">
                        {errors.tripTagId?.message}
                      </p>
                    )}
                  </CCol>
                </CRow>
                <CRow className="mb-3">
                  <CFormLabel
                    htmlFor="tripRemarks"
                    className="col-sm-4 col-form-label"
                  >
                    Remarks:
                  </CFormLabel>
                  <CCol sm={7}>
                    <CFormTextarea
                      id="tripRemarks"
                      rows={4}
                      {...register("tripRemarks")}
                    ></CFormTextarea>
                    {errors.tripRemarks?.message && (
                      <p className="error_text">
                        {errors.tripRemarks?.message}
                      </p>
                    )}
                  </CCol>
                </CRow>
              </CCol>

              <div className="d-flex justify-content-end mt-3">
                <CButton type="submit" color="primary" className="px-5 py-2">
                  Submit
                </CButton>
              </div>
            </CRow>
          </CForm>
        </CCardBody>
      </CCard>
      <CModal
        scrollable
        backdrop="static"
        visible={showTripListModal}
        onClose={() => setShowTripListModal(false)}
        alignment="center"
        size="xl"
      >
        <CModalHeader onClose={() => setShowTripListModal(false)}>
          <CModalTitle id="StaticBackdropExampleLabel">Open Trips</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <TripTableData
            isLoading={openTripQuery?.isLoading}
            data={openTripQuery?.data?.data}
            onRowClick={onRowClick}
            {...{ sortConfig, handleClickColumnSort }}
          />
        </CModalBody>
      </CModal>
    </>
  );
};

export default CreateTrip;
